import { LoanEditorActionsEnum } from 'constants/loanActionEditor';

export const isSendFormBundleAction = action =>
  (action?.loanActionTypes ?? []).some(
    ({ type }) => type === LoanEditorActionsEnum.SEND_FORM_BUNDLE,
  );

// FormBundles has no structured "category" column (dbo.FormBundles — verified against dev
// schema), so the platform's only reliable signal that a bundle IS the Initial Loan Estimate
// package is its own name. Interfirst dev names it literally "Initial Disclosures"; companies
// configure other SendFormBundles actions (a "Closing Disclosures" test action, mail-follow-up
// actions, etc.) that must never be picked instead.
const INITIAL_DISCLOSURES_BUNDLE_NAME = 'initial disclosures';

const normalizeBundleLabel = value => (value ?? '').trim().toLowerCase();

const getSendFormBundleLoanActionType = action =>
  (action?.loanActionTypes ?? []).find(
    ({ type }) => type === LoanEditorActionsEnum.SEND_FORM_BUNDLE,
  ) ?? null;

export const getSendFormBundleTargetIds = action =>
  getSendFormBundleLoanActionType(action)?.formBundles?.map(({ id }) => id) ?? [];

const targetsInitialDisclosuresBundle = (action, formBundlesById) =>
  getSendFormBundleTargetIds(action).some(
    id => normalizeBundleLabel(formBundlesById?.[id]?.name) === INITIAL_DISCLOSURES_BUNDLE_NAME,
  );

// Deterministic resolution: only ever returns a SendFormBundles action whose configured form
// bundle is confirmed (via the loan's form-bundle catalog) to be the Initial Disclosures
// package. On a company with several SendFormBundles-typed actions (a realistic, common config
// — the Interfirst dev tenant has 5+), this NEVER falls back to "the first one" — callers must
// treat a null return as "block, don't guess".
export const resolveInitialDisclosuresSendAction = (loanActions, formBundlesById) => {
  const matches = (loanActions ?? [])
    .filter(isSendFormBundleAction)
    .filter(action => targetsInitialDisclosuresBundle(action, formBundlesById));

  if (!matches.length) {
    return null;
  }

  // Stable tie-break when multiple actions target the same Initial Disclosures bundle (e.g.
  // "Send Initial Disclosures" / "VV Send ID" / "Email Initial Disclosures" all target it on
  // the Interfirst dev tenant) — alphabetical by name, then by id, so re-renders never flip
  // which action fires.
  return [...matches].sort(
    (a, b) => (a.name ?? '').localeCompare(b.name ?? '') || (a.id ?? '').localeCompare(b.id ?? ''),
  )[0];
};

export const isMailDocumentPackageAction = action =>
  (action?.loanActionTypes ?? []).some(
    ({ type }) => type === LoanEditorActionsEnum.MAIL_DOCUMENT_PACKAGE,
  );

// Same deterministic tie-break as resolveInitialDisclosuresSendAction: a company could configure
// more than one MailDocumentPackage action, and picking "the first one" by object insertion order
// would make the wizard's physical-mail dispatch flaky across renders.
export const resolveMailDocumentPackageAction = loanActions => {
  const matches = (loanActions ?? []).filter(isMailDocumentPackageAction);

  if (!matches.length) {
    return null;
  }

  return [...matches].sort(
    (a, b) => (a.name ?? '').localeCompare(b.name ?? '') || (a.id ?? '').localeCompare(b.id ?? ''),
  )[0];
};
